import mobile from 'is-mobile';
import React, { useContext, useEffect, useState } from 'react';
import { faRotateLeft } from '@fortawesome/free-solid-svg-icons';

import CustomButton from '../components/common/custom-button/custom-button.jsx';
import CustomStepper from '../components/common/custom-stepper/custom-stepper.jsx';
import LoadingSpinner from '../components/common/loading-spinner.jsx';
import RestartRegistrationModal from './components/restart-registration-modal/restart-registration-modal.jsx';
import useGtag from '../hooks/useGtag.jsx';
import { useCurrentUser } from '../store/current-user-context.jsx';
import { signupFormStore } from '../store/signup-form-reducer.jsx';
import { updateStoreWithSessionInfo } from '../utils/signup-utils.js';
import SignupRegister from './signup-register.jsx';
import SignupReview from './signup-review.jsx';
import SignupWhiteLabel from './signup-white-label.jsx';
import SignupWorkspaces from './signup-workspaces.jsx';
import '../styles/signup/signup.scss';

const steps = ['Register', 'Set Up White Label', 'Add Workspace', 'Review'];
const mobileSteps = ['Register', 'Review'];

const SignupView = () => {
  const [fetchingInfo, setFetchingInfo] = useState(true);
  const [restartRegistrationModalOpen, setRestartRegistrationModalOpen] = useState(false);
  const { user, loading, logoutAndRedirect } = useCurrentUser();
  const isLoggedIn = !!user;
  const isMobile = mobile();
  const stepsForSession = isMobile ? mobileSteps : steps;
  const { trackEventOnce } = useGtag();
  const { state, dispatch } = useContext(signupFormStore);
  useEffect(() => {
    // Mobile users can't get to white label or workspace selection steps
    if (isMobile && (state?.activeStep === 1 || state?.activeStep === 2)) {
      dispatch({ type: 'UPDATE_ACTIVE_STEP', payload: 3 });
    }
  }, [isMobile, state?.activeStep]);

  // Scroll to top when step changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [state?.activeStep]);

  useEffect(() => {
    if (state?.activeStep === 0 && user?.agencyId) {
      updateStoreWithSessionInfo(user, isMobile, setFetchingInfo, dispatch);
    }

    // already retrieved info, and theres nothing there
    if (!loading && !user?.agencyId && state?.activeStep === 0) {
      setFetchingInfo(false);
    }
  }, [user, loading]);

  return (
    <div className="signup-app-container">
      <section className="signup-stepper-container" data-testid="signup-stepper">
        <img src="../res/cc-cloud-logo-full-fade.png" />
        <CustomStepper
          steps={stepsForSession}
          activeStep={state?.activeStep}
          style={{ flex: 1, maxWidth: '724px' }}
        />
        <div className="logout-button">
          <CustomButton
            variant="tertiary"
            onClick={() => setRestartRegistrationModalOpen(true)}
            faIcon={faRotateLeft}
            style={{ visibility: isLoggedIn ? 'visible' : 'hidden' }}
            testid="restart-registration-button"
          >
            Restart Registration
          </CustomButton>
        </div>
      </section>
      {fetchingInfo ? (
        <div className="fetching-user-spinner" data-testid="fetching-user-spinner">
          <LoadingSpinner size={'32px'} />
        </div>
      ) : (
        <>
          {state?.activeStep === 0 && (
            <SignupRegister
              formData={state.formData}
              dispatch={dispatch}
              isMobile={isMobile}
              trackEventOnce={trackEventOnce}
            />
          )}
          {state?.activeStep === 1 && (
            <SignupWhiteLabel
              formData={state.formData}
              dispatch={dispatch}
              trackEventOnce={trackEventOnce}
            />
          )}
          {state?.activeStep === 2 && (
            <SignupWorkspaces
              formData={state.formData}
              dispatch={dispatch}
              trackEventOnce={trackEventOnce}
            />
          )}
          {state?.activeStep === 3 && (
            <SignupReview
              formData={state.formData}
              dispatch={dispatch}
              isMobile={isMobile}
              trackEventOnce={trackEventOnce}
            />
          )}
        </>
      )}
      <RestartRegistrationModal
        open={restartRegistrationModalOpen}
        setOpen={setRestartRegistrationModalOpen}
        logoutAndRedirect={logoutAndRedirect}
      />
    </div>
  );
};

export default SignupView;
